// Lightweight clean-URL routing (e.g. /services) using the browser history API.

export const NAVIGATE_EVENT = 'app:navigate'

// Old hash links that don't map one-to-one onto a path.
const LEGACY_HASH_REDIRECTS: Record<string, string> = {
  '#/contact/book': '/contact#book',
}

export function normalizePath(path: string) {
  const trimmed = path.replace(/\/+$/, '')
  return trimmed === '' ? '/' : trimmed
}

/** Converts an old "#/services"-style hash into its clean URL, if it is one. */
export function legacyHashToPath(hash: string): string | null {
  if (!hash.startsWith('#/')) return null
  return LEGACY_HASH_REDIRECTS[hash] ?? normalizePath(hash.slice(1))
}

/**
 * Rewrites the address bar in place (no new history entry) for old hash
 * links and trailing slashes, then returns the clean pathname.
 */
export function resolveCurrentPath() {
  const { pathname, search, hash } = window.location
  const legacy = legacyHashToPath(hash)
  if (legacy) {
    window.history.replaceState(null, '', legacy)
  } else {
    const normalized = normalizePath(pathname)
    if (normalized !== pathname) {
      window.history.replaceState(null, '', normalized + search + hash)
    }
  }
  return normalizePath(window.location.pathname)
}

export function navigate(to: string, options: { replace?: boolean } = {}) {
  const url = new URL(to, window.location.origin)
  const next = url.pathname + url.search + url.hash
  const current =
    window.location.pathname + window.location.search + window.location.hash
  if (options.replace) {
    window.history.replaceState(null, '', next)
  } else if (next !== current) {
    window.history.pushState(null, '', next)
  }
  window.dispatchEvent(new Event(NAVIGATE_EVENT))
}

/** Sends clicks on internal links (href="/...") through client-side routing. */
export function interceptLinkClicks() {
  const onClick = (event: MouseEvent) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return
    const anchor = (event.target as Element | null)?.closest?.('a')
    if (!anchor) return
    if (anchor.target && anchor.target !== '_self') return
    if (anchor.hasAttribute('download')) return
    const href = anchor.getAttribute('href')
    if (!href || !href.startsWith('/') || href.startsWith('//')) return
    event.preventDefault()
    navigate(href)
  }
  document.addEventListener('click', onClick)
  return () => document.removeEventListener('click', onClick)
}
